import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
    Box,
    Button,
    InputLabel,
    Typography,
    FormGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Slider,
  } from "@mui/material";
  import CloudUploadIcon from "@mui/icons-material/CloudUpload";
  import CropIcon from "@mui/icons-material/Crop";
  import CheckIcon from "@mui/icons-material/Check";
  import CloseIcon from "@mui/icons-material/Close";
  import Translation from "../../../utils/Translation";
  import { styled } from "@mui/material/styles";
  
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

export default function UploadImg({image,setImage,croppedImage,setCroppedImage,register,handleFileChange,setFileImg,imagePreview,setImagePreview}) {

  const [showCropper, setShowCropper] = useState(false);

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleCropComplete = (croppedDataUrl) => {
    setCroppedImage(croppedDataUrl);
    setImagePreview(croppedDataUrl);
    
    // Convert to file
    const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
    const fileName = `cropped-image-${timestamp}.webp`;
    const croppedFile = dataURLtoFile(croppedDataUrl, fileName);
    
    setFileImg(croppedFile);
    setShowCropper(false);
  };

  const handleEditImage = () => {
    setShowCropper(true);
  };
  
  return (
    <Box className="img-section" sx={{ mt: 3 }}>
      <InputLabel className="img-title" shrink htmlFor="upload-image">
        <Typography component="span" variant="b">
          <Translation data="store_seller.main_img" />
          <Typography component="b" variant="b" color="textSecondary" sx={{ m: 2, fontSize: "14px" }}>
            <Translation data="store_seller.recommended_img_size" />
            <br />
            <Typography component="small" variant="caption" color="textSecondary" sx={{ fontSize: "12px" }}>
              Recommended: High quality product images (up to 3300x4500px, 11:15 ratio)
            </Typography>
          </Typography>
        </Typography>
      </InputLabel>

      <FormGroup className="icon-arabic">
        {imagePreview && (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <img
              loading="lazy"
              src={imagePreview || require("../../../assets/image/product/default-product.webp")}
              alt="Product"
              className="max-height-img"
              style={{ display: 'block', maxHeight: '400px', width: 'auto' }}
            />
            {image && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CropIcon />}
                onClick={handleEditImage}
                sx={{ mt: 1 }}
              >
                <Translation data="store_seller.crop_img" />
              </Button>
            )}
          </Box>
        )}
        
        <Button
          className="mt-1"
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{ mt: 2 }}
        >
          <Translation data="store_seller.upload_img" />
          <VisuallyHiddenInput
            type="file"
            id="upload-image"
            accept="image/*"
            {...register("image", { required: "File is required" })}
            onChange={handleFileChange}
          />
        </Button>
      </FormGroup>

      {/* Image Cropper Dialog */}
      <CropperDialog
        open={showCropper}
        image={image}
        onClose={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
      />
    </Box>
  )
}

const CropperDialog = ({ open, image, onClose, onCropComplete }) => {
  const cropperRef = useRef(null);
  const aspectRatio = 11/15; // Fixed product ratio 11:15
  const [viewMode, setViewMode] = useState(1);

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const canvas = cropper.getCroppedCanvas({
        width: 3300, // Match Flutter maxWidth
        height: 4500, // Match Flutter maxHeight
        minWidth: 300,
        minHeight: 409, // Maintain 11:15 ratio (300 * 15/11)
        maxWidth: 3300,
        maxHeight: 4500,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      
      if (canvas) {
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            onCropComplete(reader.result);
          };
          reader.readAsDataURL(blob);
        }, 'image/webp', 0.9); // High quality WebP (90%)
      }
    }
  };

  const resetCropper = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.reset();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px'
        }
      }}
    >
      <DialogTitle>
        <Translation data="store_seller.crop_img" />
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Info Text */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Product Image Cropping
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
              Images will be cropped to 11:15 ratio for optimal display quality across the platform
            </Typography>
          </Box>

          {/* Cropper Container */}
          <Box 
            sx={{ 
              height: '400px', 
              width: '100%',
              '& .cropper-container': {
                height: '100% !important'
              }
            }}
          >
            {image && (
              <Cropper
                ref={cropperRef}
                src={image}
                style={{ height: '100%', width: '100%' }}
                aspectRatio={aspectRatio}
                viewMode={viewMode}
                guides={true}
                background={false}
                responsive={true}
                autoCropArea={0.8}
                checkOrientation={true}
                dragMode="move"
                cropBoxMovable={true}
                cropBoxResizable={true}
                toggleDragModeOnDblclick={false}
                minCanvasWidth={300}
                minCanvasHeight={409} // Maintain 11:15 ratio
                minCropBoxWidth={220} // 11:15 ratio (220 * 15/11 ≈ 300)
                minCropBoxHeight={300}
                aspectRatioLockEnabled={true} // Lock the aspect ratio
                aspectRatioLockDisabled={false}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={resetCropper}
          variant="outlined"
          color="secondary"
        >
          Reset
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCrop}
          variant="contained"
          startIcon={<CheckIcon />}
          color="primary"
        >
          Apply Crop
        </Button>
      </DialogActions>
    </Dialog>
  );
};

  
  
